import React from 'react';
import { useTranslation } from 'react-i18next';

import MyButton from '../button/Button';

export interface TariffNotAppliedWarningProperties {
  /** Optional title override */
  title?: string;
  /** Optional description override */
  description?: string;
  /** Label for the cancel button */
  cancelButtonLabel?: string;
  /** Callback invoked when the user clicks the cancel button */
  cancelButtonOnClick: () => void;
  /** Label for the add tariff button */
  submitButtonLabel?: string;
  /** Callback invoked when the user clicks the add tariff button */
  submitButtonOnClick: () => void;
}

/**
 * TariffNotAppliedWarning component.
 * Displays a warning notification dialog when a charger is added successfully
 * but has no tariff assigned, providing actions to cancel or navigate to add tariff.
 * @param {TariffNotAppliedWarningProperties} properties - Component properties.
 * @param {string} [properties.title] - Dialog title.
 * @param {string} [properties.description] - Dialog explanatory message.
 * @param {string} [properties.cancelButtonLabel] - Text label for cancel button.
 * @param {() => void} properties.cancelButtonOnClick - Cancel button handler.
 * @param {string} [properties.submitButtonLabel] - Text label for add tariff button.
 * @param {() => void} properties.submitButtonOnClick - Add tariff button handler.
 * @returns {React.JSX.Element} The rendered TariffNotAppliedWarning component.
 */
function TariffNotAppliedWarning(
  properties: TariffNotAppliedWarningProperties,
): React.JSX.Element {
  const { t } = useTranslation('partnerDeviceManagement');
  const {
    title,
    description,
    cancelButtonLabel,
    cancelButtonOnClick,
    submitButtonLabel,
    submitButtonOnClick,
  } = properties;

  return (
    <div className="tariff-not-applied-warning-wrap">
      <div className="tariff-not-applied-warning-content">
        <div className="warning-icon-wrap" aria-hidden="true">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h2>{title || t('tariffNotApplied', 'Tariff Not Applied')}</h2>
        <p>
          {description ||
            t(
              'tariffNotAppliedDescription',
              'This charger has been added successfully, but no tariff has been applied. Please configure a tariff to enable charging.',
            )}
        </p>
      </div>
      <div className="tariff-not-applied-buttons">
        <MyButton
          label={cancelButtonLabel || t('cancel', 'Cancel')}
          buttonType="back"
          onClickFunc={cancelButtonOnClick}
          disableHoverEffect
        />
        <MyButton
          label={submitButtonLabel || t('addTariff', 'Add Tariff')}
          buttonType="primary"
          onClickFunc={submitButtonOnClick}
        />
      </div>
    </div>
  );
}

export default TariffNotAppliedWarning;

